import { useEffect, useState } from 'react';
import { useRevalidator } from 'react-router';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { Slider, Switch } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { FlagDeModulo } from '@/data/tipos';
import { CampoDeMotivo, motivoValido } from './apoioDoPainel';

function mensagem(e: unknown): string {
    return e instanceof Error ? e.message : 'Tente de novo.';
}

/**
 * Rollout por porcentagem.
 *
 * A escolha de quem cai dentro é por hash estável de (flag, clínica): a mesma
 * clínica não entra e sai a cada leitura. Enquanto houver rollout, o padrão da
 * base não vale — e a tela diz isso em vez de manter um interruptor que não faz nada.
 */
export function RolloutModal({ flag, total, onFechar }: { flag: FlagDeModulo | null; total: number; onFechar: () => void }) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [usar, setUsar] = useState(false);
    const [percentual, setPercentual] = useState(0);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (flag) {
            setUsar(flag.rolloutPercentual !== undefined);
            setPercentual(flag.rolloutPercentual ?? 10);
            setErro(null);
        }
    }, [flag]);

    const mudou = flag ? (usar ? flag.rolloutPercentual !== percentual : flag.rolloutPercentual !== undefined) : false;

    const salvar = async () => {
        if (!flag) return;
        setSalvando(true);
        setErro(null);
        try {
            const nova = await dados().definirRolloutDaFlag(flag.id, usar ? percentual : null);
            aviso({
                title: usar ? `Rollout em ${percentual}%` : 'Rollout removido',
                description: usar
                    ? `${flag.chave} · ligada em ${nova.ligadas ?? '?'} de ${nova.total ?? total} clínicas`
                    : `${flag.chave} volta a seguir o padrão da base`,
            });
            onFechar();
            void revalidate();
        } catch (e) {
            setErro(mensagem(e));
        } finally {
            setSalvando(false);
        }
    };

    const estimativa = Math.round((total * percentual) / 100);

    return (
        <Modal
            open={flag !== null} onClose={onFechar} width={500}
            title={flag ? `Rollout de ${flag.chave}` : 'Rollout'}
            description="Liga o módulo para uma parte das clínicas, escolhida por hash estável — a mesma clínica não oscila."
            dirty={mudou}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button loading={salvando} disabled={!mudou} onClick={() => { void salvar(); }}>Salvar</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <Switch
                    checked={usar} onChange={setUsar}
                    label="Usar rollout por porcentagem"
                    description={usar ? 'o padrão da base deixa de valer enquanto houver rollout' : 'a flag segue o padrão da base'}
                />
                {usar && (
                    <>
                        <Slider label="Percentual das clínicas" value={percentual} onChange={setPercentual} min={0} max={100} step={1} suffix="%" />
                        <Alert tone={percentual > 50 ? 'warning' : 'neutral'} dismissible={false} title={`Cerca de ${estimativa} de ${total} clínicas`}>
                            As exceções por clínica continuam mandando. A conta exata de quem ficou ligada aparece
                            depois de salvar{percentual > 50 ? ' — e acima de 50% o kill switch é a volta rápida se algo quebrar' : ''}.
                        </Alert>
                    </>
                )}
            </div>
        </Modal>
    );
}

/**
 * Kill switch: desliga o módulo para TODAS as clínicas, exceções inclusive, na
 * hora e sem deploy. Religar devolve o que valia antes — padrão, exceção, rollout.
 */
export function KillSwitchModal({ flag, onFechar }: { flag: FlagDeModulo | null; onFechar: () => void }) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (flag) {
            setMotivo('');
            setErro(null);
        }
    }, [flag]);

    const desligar = !flag?.desligadaEm;

    const confirmar = async () => {
        if (!flag) return;
        setSalvando(true);
        setErro(null);
        try {
            await dados().acionarKillSwitch(flag.id, desligar, motivo);
            aviso({
                title: desligar ? 'Kill switch acionado' : 'Módulo religado',
                description: `${flag.chave} · registrado na auditoria`,
                tone: desligar ? 'warning' : 'success',
            });
            onFechar();
            void revalidate();
        } catch (e) {
            setErro(mensagem(e));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={flag !== null} onClose={onFechar} width={480} tone={desligar ? 'danger' : 'default'}
            title={desligar ? 'Acionar o kill switch?' : 'Religar o módulo?'}
            description={flag
                ? (desligar
                    ? `${flag.chave} sai de TODAS as clínicas agora — exceções inclusive.`
                    : `${flag.chave} volta a seguir padrão, exceções e rollout, como antes do kill switch.`)
                : ''}
            dirty={motivo.trim().length > 0}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button variant={desligar ? 'danger' : 'primary'} loading={salvando} disabled={!motivoValido(motivo)} onClick={() => { void confirmar(); }}>
                        {desligar ? 'Desligar para todas' : 'Religar'}
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                {flag?.desligadaMotivo && !desligar && (
                    <Alert tone="neutral" dismissible={false} title="Por que foi desligado">{flag.desligadaMotivo}</Alert>
                )}
                <CampoDeMotivo id="kill-motivo" valor={motivo} onChange={setMotivo} dica="Fica no log de auditoria. Escreva ao menos 10 caracteres." />
            </div>
        </Modal>
    );
}
